import { useState, useEffect, useRef, useCallback } from 'react';

export const useWebSocket = (url) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Bağlanıyor...');
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const shouldReconnectRef = useRef(true);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionStatus('Bağlandı');
        setError(null);
        reconnectAttempts.current = 0;
        console.log('WebSocket bağlantısı kuruldu:', url);
      };

      ws.onmessage = (event) => {
        try {
          const receivedData = JSON.parse(event.data);
          
          // Gelen veri direkt array ise onu kullan
          if (Array.isArray(receivedData)) {
            setData(receivedData);
          } else if (receivedData.type && receivedData.data) {
            // Eski format için
            setData(prevData => ({
              ...prevData,
              [receivedData.type]: receivedData.data
            }));
          } else if (receivedData.data && Array.isArray(receivedData.data)) {
            // Nested data formatı için
            setData(receivedData.data);
          } else {
            setData(receivedData);
          }
        } catch (parseError) {
          console.error('WebSocket veri parse hatası:', parseError);
          setError('Veri formatı hatası');
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setConnectionStatus('Bağlantı kesildi');
        console.log('WebSocket bağlantısı kapandı:', event.code, event.reason);
        
        // Sadece normal olmayan kapanmalarda yeniden bağlan
        if (shouldReconnectRef.current && event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            setConnectionStatus(`Yeniden bağlanılıyor... (${reconnectAttempts.current}/${maxReconnectAttempts})`);
            connect();
          }, delay);
        } else if (event.code === 1000) {
          setConnectionStatus('Bağlantı kapatıldı');
        } else {
          setError('Maksimum yeniden bağlanma denemesi aşıldı');
          setConnectionStatus('Bağlantı başarısız');
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket hatası:', event);
        setError('Bağlantı hatası oluştu');
        setConnectionStatus('Hata');
      };

    } catch (error) {
      console.error('WebSocket oluşturma hatası:', error);
      setError('WebSocket oluşturulamadı');
      setConnectionStatus('Oluşturma hatası');
    }
  }, [url]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Mesaj gönderme hatası:', error);
        return false;
      }
    }
    return false;
  }, []);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectAttempts.current = 0;
    setError(null);
    setConnectionStatus('Yeniden bağlanılıyor...');
    connect();
  }, [connect]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
    setIsConnected(false);
    setConnectionStatus('Bağlantı kesildi');
    reconnectAttempts.current = 0;
  }, []);

  return {
    isConnected,
    connectionStatus,
    data,
    error,
    sendMessage,
    reconnect,
    disconnect
  };
};

